import { Button } from '@progress/kendo-react-buttons';
import { IconThemeColor, SvgIcon } from '@progress/kendo-react-common';
import { InputPrefix, InputSuffix, TextBox } from '@progress/kendo-react-inputs';
import clsx from 'clsx';
import React from 'react';
import { EventBus } from '../../app/contexts/event-bus';
import { FileSystemExtendedAvailable } from '../../common/helpers';
import { TrashOutline, WarningTriangleOutline } from '../../icons/icons';
import { getDirectoryDb } from '../../services/directory-db';

export interface DirectoryPickerProps {
    readonly indexedDbKey: string;
    title?: React.ReactNode;
    placeholder?: string;
    className?: string;
}

export default function DirectoryPicker(props: DirectoryPickerProps) {
    const [handle, setHandle] = React.useState<FileSystemDirectoryHandle | null>(null);
    const [permission, setPermission] = React.useState<PermissionState>('granted');

    React.useEffect(() => {
        const load = async () => {
            const db = await getDirectoryDb();
            const handle = await db.get('file-handles', props.indexedDbKey);
            if (handle && handle.kind === 'directory') {
                setHandle(handle);
                setPermission(await handle.queryPermission({ mode: 'readwrite' }));
            }
        };
        load();
    }, []);

    const handleEdit = async () => {
        const dir = await window.showDirectoryPicker();
        if ((await dir.requestPermission({ mode: 'readwrite' })) === 'granted') {
            setHandle(dir);
            setPermission(await dir.queryPermission({ mode: 'readwrite' }));
            const db = await getDirectoryDb();
            await  db.put('file-handles', dir, props.indexedDbKey);
            EventBus.fire('file-handle-changed', props.indexedDbKey);
        }
    };

    const handleRemovePath = async () => {
        const db = await getDirectoryDb();
        await db.delete('file-handles', props.indexedDbKey);
        setHandle(null);
        setPermission('granted');
        EventBus.fire('file-handle-changed', props.indexedDbKey);
    };

    const handleRequestPermission = async () => {
        setPermission(await handle!.requestPermission({ mode: 'readwrite' }));
        EventBus.fire('file-handle-changed', props.indexedDbKey);
    };

    return (
        <div className={clsx('py-2 w-100', props.className)}>
            <div className="d-flex gap-2 w-100 align-items-center">
                <div className="flex-shrink-0">{props.title}</div>
                <TextBox
                    disabled={!FileSystemExtendedAvailable}
                    onClick={handleEdit}
                    className="flex-grow-1 cursor-pointer"
                    value={handle ? '/' + handle.name : ''}
                    readOnly={true}
                    prefix={() => (
                        <InputPrefix>
                            {permission !== 'granted' && (
                                <SvgIcon icon={WarningTriangleOutline} size="large" themeColor={permissionToColor(permission)}></SvgIcon>
                            )}
                        </InputPrefix>
                    )}
                    suffix={() => (
                        <InputSuffix>
                            {handle && (
                                <Button
                                    svgIcon={TrashOutline}
                                    fillMode={'outline'}
                                    themeColor={'error'}
                                    onClick={handleRemovePath}></Button>
                            )}
                        </InputSuffix>
                    )}
                    placeholder={props.placeholder || 'Click to set folder'}></TextBox>
                {permission === 'prompt' && (
                    <div>
                        <Button fillMode={'outline'} themeColor={'primary'} onClick={handleRequestPermission}>
                            Grant permission
                        </Button>
                    </div>
                )}
            </div>
            {!FileSystemExtendedAvailable && (
                <div className="d-flex align-items-center secondary-text pt-2">
                    <SvgIcon icon={WarningTriangleOutline} size="large"></SvgIcon>
                    Feature unavailable in this browser
                </div>
            )}
        </div>
    );
}

function permissionToColor(permission: PermissionState): IconThemeColor {
    return permission === 'denied' ? 'error' : permission === 'prompt' ? 'warning' : 'inherit';
}
